import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, Download, Home, Map, MessageSquare, Network, Newspaper, RefreshCw, Settings, TrendingUp, Users, Wallet, Wrench } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import ktcLogo from '../../assets/ktc.png';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import StaffPageHeader from '../../components/StaffPageHeader';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const MONTHS = [
  { value: '1', label: 'Januari' },
  { value: '2', label: 'Februari' },
  { value: '3', label: 'Maret' },
  { value: '4', label: 'April' },
  { value: '5', label: 'Mei' },
  { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' },
  { value: '8', label: 'Agustus' },
  { value: '9', label: 'September' },
  { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' },
  { value: '12', label: 'Desember' },
];

const TABS = [
  { key: 'topup_reseller', label: 'Topup & Komisi Reseller' },
  { key: 'income_harian', label: 'Income Harian' },
  { key: 'income_periode', label: 'Income Periode' },
  { key: 'pengeluaran', label: 'Pengeluaran' },
  { key: 'laba_rugi', label: 'Laba Rugi' },
  { key: 'bhp_uso', label: 'Hitung BHP / USO' },
];

const fmtCurrency = (value) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Number(value) || 0);

const AdminFinance = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('income_periode');
  const [loading, setLoading] = useState(true);
  const [loadingReseller, setLoadingReseller] = useState(false);
  const [filterMonth, setFilterMonth] = useState(String(new Date().getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [incomeScope, setIncomeScope] = useState('combined');
  const [incomeFull, setIncomeFull] = useState({});
  const [financeOverview, setFinanceOverview] = useState(null);
  const [financeBreakdowns, setFinanceBreakdowns] = useState({ by_source: [], by_payment_method: [], by_reseller: [], by_site: [] });
  const [receivables, setReceivables] = useState({ total: 0, count: 0, items: [] });
  const [todayIncome, setTodayIncome] = useState(0);
  const [topupReseller, setTopupReseller] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseForm, setExpenseForm] = useState({ name: '', amount: '', note: '' });
  const [bhpPercent, setBhpPercent] = useState('1.25');
  const [usoPercent, setUsoPercent] = useState('1.25');
  const [topupPage, setTopupPage] = useState(1);
  const [topupPerPage, setTopupPerPage] = useState(20);
  const [expensePage, setExpensePage] = useState(1);
  const [expensePerPage, setExpensePerPage] = useState(20);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const totalExpense = useMemo(
    () => expenses
      .filter((item) => ['approved', 'active'].includes(String(item?.approval_status || item?.status || '').toLowerCase()))
      .reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [expenses],
  );
  const totalTopupPages = Math.max(1, Math.ceil(topupReseller.length / topupPerPage));
  const pagedTopupReseller = useMemo(() => {
    const start = (topupPage - 1) * topupPerPage;
    return topupReseller.slice(start, start + topupPerPage);
  }, [topupReseller, topupPage, topupPerPage]);
  const totalExpensePages = Math.max(1, Math.ceil(expenses.length / expensePerPage));
  const pagedExpenses = useMemo(() => {
    const start = (expensePage - 1) * expensePerPage;
    return expenses.slice(start, start + expensePerPage);
  }, [expenses, expensePage, expensePerPage]);
  const grossIncome = Number(financeOverview?.summary?.total_income ?? incomeFull.gross_income ?? 0);
  const netIncome = Number(financeOverview?.summary?.net_profit_loss ?? incomeFull.net_income ?? 0);
  const bhpAmount = grossIncome * ((Number(bhpPercent) || 0) / 100);
  const usoAmount = grossIncome * ((Number(usoPercent) || 0) / 100);
  const bySource = useMemo(() => {
    const rows = Array.isArray(financeBreakdowns?.by_source) ? financeBreakdowns.by_source : [];
    return rows.reduce((acc, row) => ({ ...acc, [String(row?.key || '')]: row }), {});
  }, [financeBreakdowns?.by_source]);
  const incomePppoe = Number(bySource.pppoe_payment?.income ?? incomeFull.income_pppoe ?? 0);
  const incomeVoucher = Number(bySource.voucher_sale?.income ?? incomeFull.income_voucher ?? 0);
  const incomeTopup = Number(bySource.reseller_topup?.income ?? 0);
  const commissionVoucher = Number(bySource.reseller_commission?.expense ?? incomeFull.agent_commission ?? 0);
  const combinedGross = Number(financeOverview?.summary?.total_income ?? incomeFull.gross_income ?? 0);
  const combinedNet = Number(financeOverview?.summary?.net_profit_loss ?? incomeFull.net_income ?? 0);
  const voucherNet = incomeVoucher - commissionVoucher;
  const periodLabel = String(financeOverview?.period?.label || incomeFull?.period?.label || '').trim() || `${String(filterMonth).padStart(2, '0')}/${filterYear}`;
  const methodBreakdown = (Array.isArray(financeBreakdowns?.by_payment_method) ? financeBreakdowns.by_payment_method : []).map((item) => [item.key, item.income || 0]);
  const receivableAgingBuckets = useMemo(() => {
    const raw = receivables?.aging_buckets && typeof receivables.aging_buckets === 'object'
      ? receivables.aging_buckets
      : {};
    const ordered = ['current', '1_30', '31_60', '61_90', '90_plus'];
    return ordered.map((key) => {
      const bucket = raw[key] && typeof raw[key] === 'object' ? raw[key] : {};
      return {
        key,
        label: String(bucket?.label || (
          key === 'current' ? 'Belum overdue'
            : key === '1_30' ? '1-30 hari'
              : key === '31_60' ? '31-60 hari'
                : key === '61_90' ? '61-90 hari'
                  : '> 90 hari'
        )),
        amount: Number(bucket?.amount || 0),
        count: Number(bucket?.count || 0),
      };
    });
  }, [receivables?.aging_buckets]);

  const fetchIncomePeriode = useCallback(async () => {
    const params = {
      month: Number(filterMonth),
      year: Number(filterYear),
    };
    if (dateFrom || dateTo) {
      params.date_from = dateFrom || dateTo;
      params.date_to = dateTo || dateFrom;
    }
    const [overviewRes, breakdownRes, receivableRes] = await Promise.all([
      axios.get(`${API}/finance/overview`, { headers: getHeaders(), params }),
      axios.get(`${API}/finance/breakdowns`, { headers: getHeaders(), params }),
      axios.get(`${API}/finance/receivables`, { headers: getHeaders(), params }),
    ]);
    const [pppoeLedgerRes, voucherLedgerRes] = await Promise.all([
      axios.get(`${API}/finance/ledger`, { headers: getHeaders(), params: { ...params, source_type: 'pppoe_payment', entry_type: 'income', limit: 500 } }),
      axios.get(`${API}/finance/ledger`, { headers: getHeaders(), params: { ...params, source_type: 'voucher_sale', entry_type: 'income', limit: 500 } }),
    ]);

    const overviewPayload = overviewRes?.data && typeof overviewRes.data === 'object' ? overviewRes.data : {};
    const breakdownPayload = breakdownRes?.data && typeof breakdownRes.data === 'object' ? breakdownRes.data : {};
    const receivablePayload = receivableRes?.data && typeof receivableRes.data === 'object' ? receivableRes.data : {};

    setFinanceOverview(overviewPayload);
    setFinanceBreakdowns({
      by_source: Array.isArray(breakdownPayload.by_source) ? breakdownPayload.by_source : [],
      by_payment_method: Array.isArray(breakdownPayload.by_payment_method) ? breakdownPayload.by_payment_method : [],
      by_reseller: Array.isArray(breakdownPayload.by_reseller) ? breakdownPayload.by_reseller : [],
      by_site: Array.isArray(breakdownPayload.by_site) ? breakdownPayload.by_site : [],
    });
    setReceivables({
      total: Number(receivablePayload.total || 0),
      count: Number(receivablePayload.count || 0),
      items: Array.isArray(receivablePayload.items) ? receivablePayload.items : [],
    });

    // compatibility object for existing UI blocks
    setIncomeFull({
      period: overviewPayload.period || {},
      gross_income: Number(overviewPayload?.summary?.total_income || 0),
      net_income: Number(overviewPayload?.summary?.net_profit_loss || 0),
      income_pppoe: Number((breakdownPayload.by_source || []).find((row) => row?.key === 'pppoe_payment')?.income || 0),
      income_voucher: Number((breakdownPayload.by_source || []).find((row) => row?.key === 'voucher_sale')?.income || 0),
      agent_commission: Number((breakdownPayload.by_source || []).find((row) => row?.key === 'reseller_commission')?.expense || 0),
      accounts_receivable: Number(receivablePayload.total || 0),
      piutang: Number(receivablePayload.total || 0),
      total_paid: Number(pppoeLedgerRes?.data?.count || 0),
      voucher_sales_count: Number(voucherLedgerRes?.data?.count || 0),
      payment_method_breakdown: (Array.isArray(breakdownPayload.by_payment_method) ? breakdownPayload.by_payment_method : []).reduce((acc, row) => {
        acc[row.key] = Number(row.income || 0);
        return acc;
      }, {}),
    });
  }, [filterMonth, filterYear, dateFrom, dateTo, getHeaders]);

  const fetchTodayIncome = useCallback(async () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const response = await axios.get(`${API}/finance/ledger`, {
      headers: getHeaders(),
      params: { date_from: today, date_to: today, entry_type: 'income', limit: 500 },
    });
    const paid = Array.isArray(response?.data?.items) ? response.data.items : [];
    const total = paid
      .filter((item) => String(item?.recognized_at || '').slice(0, 10) === today)
      .reduce((sum, item) => sum + (Number(item?.amount || 0)), 0);
    setTodayIncome(total);
  }, [getHeaders]);

  const fetchExpenses = useCallback(async () => {
    const params = {
      month: Number(filterMonth),
      year: Number(filterYear),
      limit: 1000,
    };
    if (dateFrom || dateTo) {
      params.date_from = dateFrom || dateTo;
      params.date_to = dateTo || dateFrom;
    }
    const response = await axios.get(`${API}/finance/expenses`, { headers: getHeaders(), params });
    setExpenses(Array.isArray(response?.data?.items) ? response.data.items : []);
  }, [getHeaders, filterMonth, filterYear, dateFrom, dateTo]);

  const fetchTopupReseller = useCallback(async () => {
    setLoadingReseller(true);
    try {
      const params = {
        month: Number(filterMonth),
        year: Number(filterYear),
      };
      if (dateFrom || dateTo) {
        params.date_from = dateFrom || dateTo;
        params.date_to = dateTo || dateFrom;
      }
      const [topupRes, commissionRes] = await Promise.all([
        axios.get(`${API}/finance/ledger`, { headers: getHeaders(), params: { ...params, source_type: 'reseller_topup', entry_type: 'income', limit: 300 } }),
        axios.get(`${API}/finance/ledger`, { headers: getHeaders(), params: { ...params, source_type: 'reseller_commission', entry_type: 'expense', limit: 300 } }),
      ]);
      const topups = Array.isArray(topupRes?.data?.items) ? topupRes.data.items : [];
      const commissions = Array.isArray(commissionRes?.data?.items) ? commissionRes.data.items : [];
      const mappedTopups = topups.map((row) => ({
        id: row.id,
        created_at: row.recognized_at,
        owner_name: row.reseller_name || row.reseller_id || '-',
        status: 'approved',
        commission_amount: Number(row.amount || 0),
      }));
      const mappedCommissions = commissions.map((row) => ({
        id: row.id,
        created_at: row.recognized_at,
        owner_name: row.reseller_name || row.reseller_id || '-',
        status: row.status || 'pending',
        commission_amount: Number(row.amount || 0),
      }));
      setTopupReseller([...mappedTopups, ...mappedCommissions].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))).slice(0, 500));
    } catch {
      setTopupReseller([]);
    } finally {
      setLoadingReseller(false);
    }
  }, [getHeaders, filterMonth, filterYear, dateFrom, dateTo]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([fetchIncomePeriode(), fetchTodayIncome(), fetchTopupReseller(), fetchExpenses()]);
    } catch {
      toast.error('Gagal memuat data keuangan');
    } finally {
      setLoading(false);
    }
  }, [fetchIncomePeriode, fetchTodayIncome, fetchTopupReseller, fetchExpenses]);

  useEffect(() => {
    const raw = localStorage.getItem('ktc_user');
    if (raw) {
      try {
        setUser(JSON.parse(raw));
      } catch {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveExpense = async () => {
    const name = String(expenseForm.name || '').trim();
    const amount = Number(expenseForm.amount || 0);
    if (!name || !Number.isFinite(amount) || amount <= 0) {
      toast.error('Nama pengeluaran dan nominal wajib diisi');
      return;
    }

    try {
      await axios.post(
        `${API}/finance/expenses`,
        {
          category: 'operasional',
          subcategory: 'manual',
          name,
          amount,
          notes: String(expenseForm.note || '').trim(),
        },
        { headers: getHeaders() },
      );
      setExpenseForm({ name: '', amount: '', note: '' });
      toast.success('Pengeluaran disimpan, menunggu persetujuan');
      await loadAll();
    } catch {
      toast.error('Gagal menyimpan pengeluaran');
    }
  };

  const handleApproveExpense = async (id) => {
    try {
      await axios.post(`${API}/finance/expenses/${id}/approve`, {}, { headers: getHeaders() });
      toast.success('Pengeluaran disetujui');
      await loadAll();
    } catch {
      toast.error('Gagal menyetujui pengeluaran');
    }
  };

  const handleRejectExpense = async (id) => {
    try {
      await axios.post(`${API}/finance/expenses/${id}/reject`, {}, { headers: getHeaders() });
      toast.success('Pengeluaran ditolak');
      await loadAll();
    } catch {
      toast.error('Gagal menolak pengeluaran');
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await axios.delete(`${API}/finance/expenses/${id}`, { headers: getHeaders() });
      setExpenses((prev) => prev.filter((item) => item.id !== id));
      toast.success('Pengeluaran dibatalkan');
      await loadAll();
    } catch {
      toast.error('Gagal menghapus pengeluaran');
    }
  };
  const handleExportFinanceCsv = async () => {
    const params = new URLSearchParams();
    params.set('month', String(Number(filterMonth) || new Date().getMonth() + 1));
    params.set('year', String(Number(filterYear) || new Date().getFullYear()));
    if (dateFrom || dateTo) {
      params.set('date_from', dateFrom || dateTo);
      params.set('date_to', dateTo || dateFrom);
    }
    try {
      const response = await axios.get(`${API}/finance/reports/export.csv`, {
        headers: getHeaders(),
        params: Object.fromEntries(params.entries()),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const fileName = `finance-report-${String(filterYear)}-${String(filterMonth).padStart(2, '0')}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Laporan CSV berhasil diunduh');
    } catch {
      toast.error('Gagal mengunduh laporan CSV');
    }
  };
  useEffect(() => {
    setTopupPage(1);
  }, [topupPerPage, topupReseller.length]);
  useEffect(() => {
    if (topupPage > totalTopupPages) setTopupPage(totalTopupPages);
  }, [topupPage, totalTopupPages]);
  useEffect(() => {
    setExpensePage(1);
  }, [expensePerPage, expenses.length]);
  useEffect(() => {
    if (expensePage > totalExpensePages) setExpensePage(totalExpensePages);
  }, [expensePage, totalExpensePages]);

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data keuangan..." />
      )}
      

      <AppShell>
      <PageContainer>
        <StaffPageHeader
          eyebrow="Keuangan"
          title="Keuangan"
          subtitle="Topup reseller, income, pengeluaran, laba rugi, dan hitung BHP/USO."
          actions={(
            <div className="flex items-center gap-2">
              <Button onClick={handleExportFinanceCsv} variant="outline" className="btn-secondary">
                <Download className="w-4 h-4 mr-2" />Ekspor CSV
              </Button>
              <Button onClick={loadAll} variant="outline" className="btn-secondary" disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Muat Ulang
              </Button>
            </div>
          )}
        />

        <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3">
            <p className="text-xs text-emerald-100/80">Total Income</p>
            <p className="text-3xl xl:text-4xl font-extrabold font-mono tracking-tight text-emerald-200">{fmtCurrency(financeOverview?.summary?.total_income || 0)}</p>
          </div>
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3">
            <p className="text-xs text-amber-100/80">Total Expense</p>
            <p className="text-3xl xl:text-4xl font-extrabold font-mono tracking-tight text-amber-200">{fmtCurrency(financeOverview?.summary?.total_expense || 0)}</p>
          </div>
          <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3">
            <p className="text-xs text-cyan-100/80">Net Profit/Loss</p>
            <p className="text-3xl xl:text-4xl font-extrabold font-mono tracking-tight text-cyan-200">{fmtCurrency(financeOverview?.summary?.net_profit_loss || 0)}</p>
          </div>
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3">
            <p className="text-xs text-red-100/80">Receivables</p>
            <p className="text-3xl xl:text-4xl font-extrabold font-mono tracking-tight text-red-200">{fmtCurrency(financeOverview?.summary?.receivables || 0)}</p>
          </div>
          <div className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-4 py-3">
            <p className="text-xs text-violet-100/80">Komisi Reseller Belum Dibayar</p>
            <p className="text-3xl xl:text-4xl font-extrabold font-mono tracking-tight text-violet-200">{fmtCurrency(financeOverview?.summary?.unpaid_reseller_commission || 0)}</p>
          </div>
        </div>

        <div className="finance-tab-grid grid sm:grid-cols-2 xl:grid-cols-6 gap-2">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${tab === item.key ? 'bg-brand-blue/10 text-brand-blue border-brand-blue/30' : 'bg-[#121212] text-gray-300 border-white/10 hover:border-white/20'}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="bg-[#121212] rounded-xl border border-white/10 p-4 md:p-6 space-y-4">
          {(tab === 'income_periode' || tab === 'laba_rugi' || tab === 'bhp_uso') && (
            <div className="flex flex-wrap gap-3">
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-44 bg-[#0A0A0A] border-white/10 text-white"><SelectValue placeholder="Bulan" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">{MONTHS.map((m) => <SelectItem key={m.value} value={m.value} className="text-white">{m.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="w-32 bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44 bg-[#0A0A0A] border-white/10 text-white" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44 bg-[#0A0A0A] border-white/10 text-white" />
              <Button onClick={loadAll} className="btn-primary">Terapkan</Button>
              <Button
                type="button"
                variant="outline"
                className="btn-secondary"
                onClick={() => {
                  const currentMonth = String(new Date().getMonth() + 1);
                  const currentYear = String(new Date().getFullYear());
                  setFilterMonth(currentMonth);
                  setFilterYear(currentYear);
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Reset Periode
              </Button>
            </div>
          )}

          {tab === 'income_harian' && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <p className="text-gray-400 text-sm">Income Hari Ini</p>
                <p className="text-2xl font-bold text-green-300 mt-1">{fmtCurrency(todayIncome)}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <p className="text-gray-400 text-sm">Tagihan Lunas (Periode Filter)</p>
                <p className="text-2xl font-bold text-cyan-300 mt-1">{Number(incomeFull.total_paid || 0)}</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <p className="text-gray-400 text-sm">Piutang (Periode Filter)</p>
                <p className="text-2xl font-bold text-yellow-300 mt-1">{fmtCurrency(incomeFull.accounts_receivable || 0)}</p>
              </div>
            </div>
          )}

          {tab === 'income_periode' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <p className="text-xs text-gray-400">Periode laporan aktif</p>
                <p className="text-white font-semibold">{periodLabel}</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                <button type="button" onClick={() => setIncomeScope('pppoe')} className={`rounded-lg border px-3 py-2 text-sm text-left ${incomeScope === 'pppoe' ? 'bg-cyan-500/15 text-cyan-200 border-cyan-400/50' : 'bg-[#0A0A0A] border-white/10 text-gray-300'}`}>PPPoE</button>
                <button type="button" onClick={() => setIncomeScope('voucher')} className={`rounded-lg border px-3 py-2 text-sm text-left ${incomeScope === 'voucher' ? 'bg-violet-500/15 text-violet-200 border-violet-400/50' : 'bg-[#0A0A0A] border-white/10 text-gray-300'}`}>Voucher</button>
                <button type="button" onClick={() => setIncomeScope('combined')} className={`rounded-lg border px-3 py-2 text-sm text-left ${incomeScope === 'combined' ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/50' : 'bg-[#0A0A0A] border-white/10 text-gray-300'}`}>Gabungan</button>
              </div>

              {incomeScope === 'pppoe' && (
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">PPPoE Prepaid</p><p className="text-xl font-bold text-emerald-300 mt-1">{fmtCurrency(incomeFull.income_prepaid || 0)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">PPPoE Postpaid</p><p className="text-xl font-bold text-cyan-300 mt-1">{fmtCurrency(incomeFull.income_postpaid || 0)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Total PPPoE</p><p className="text-xl font-bold text-blue-300 mt-1">{fmtCurrency(incomePppoe)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Tagihan Lunas</p><p className="text-xl font-bold text-slate-200 mt-1">{Number(incomeFull.billings_paid_count || 0)}</p></div>
                </div>
              )}

              {incomeScope === 'voucher' && (
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Gross Voucher</p><p className="text-xl font-bold text-violet-300 mt-1">{fmtCurrency(incomeVoucher)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Komisi Voucher</p><p className="text-xl font-bold text-amber-300 mt-1">{fmtCurrency(commissionVoucher)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Net Voucher</p><p className="text-xl font-bold text-green-300 mt-1">{fmtCurrency(voucherNet)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Voucher Terjual</p><p className="text-xl font-bold text-slate-200 mt-1">{Number(incomeFull.voucher_sales_count || 0)}</p></div>
                </div>
              )}

              {incomeScope === 'combined' && (
                <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Gross Gabungan</p><p className="text-xl font-bold text-cyan-300 mt-1">{fmtCurrency(combinedGross)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Income Topup Reseller</p><p className="text-xl font-bold text-sky-300 mt-1">{fmtCurrency(incomeTopup)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Beban Komisi</p><p className="text-xl font-bold text-amber-300 mt-1">{fmtCurrency(commissionVoucher)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Net Gabungan</p><p className="text-xl font-bold text-green-300 mt-1">{fmtCurrency(combinedNet)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Piutang</p><p className="text-xl font-bold text-red-300 mt-1">{fmtCurrency(receivables.total || incomeFull.piutang || 0)}</p></div>
                </div>
              )}

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm text-gray-300">Aging Piutang</p>
                  <p className="text-xs text-gray-500">Total: {fmtCurrency(receivables.total || 0)}</p>
                </div>
                <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-2">
                  {receivableAgingBuckets.map((bucket) => (
                    <div key={bucket.key} className="rounded border border-white/10 px-3 py-2">
                      <p className="text-[11px] text-gray-400">{bucket.label}</p>
                      <p className="text-sm font-semibold text-white">{fmtCurrency(bucket.amount)}</p>
                      <p className="text-[11px] text-gray-500">{bucket.count} invoice</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                <p className="text-sm text-gray-300 mb-2">Breakdown metode pembayaran PPPoE</p>
                {methodBreakdown.length === 0 ? (
                  <p className="text-xs text-gray-500">Belum ada data metode pembayaran pada periode ini.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
                    {methodBreakdown.map(([method, amount]) => (
                      <div key={method} className="rounded border border-white/10 px-3 py-2">
                        <p className="text-xs text-gray-400 uppercase">{method}</p>
                        <p className="text-sm font-semibold text-white">{fmtCurrency(amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid xl:grid-cols-3 gap-4">
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                  <p className="text-sm text-gray-300 mb-2">Breakdown Sumber</p>
                  {(financeBreakdowns.by_source || []).length === 0 ? (
                    <p className="text-xs text-gray-500">Belum ada data.</p>
                  ) : (
                    <div className="space-y-2">
                      {(financeBreakdowns.by_source || []).slice(0, 6).map((row) => (
                        <div key={`src-${row.key}`} className="flex items-center justify-between text-xs border border-white/10 rounded px-2 py-1">
                          <span className="text-gray-300">{row.key}</span>
                          <span className="text-white font-medium">{fmtCurrency(row.net)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                  <p className="text-sm text-gray-300 mb-2">Breakdown Reseller</p>
                  {(financeBreakdowns.by_reseller || []).length === 0 ? (
                    <p className="text-xs text-gray-500">Belum ada data reseller.</p>
                  ) : (
                    <div className="space-y-2">
                      {(financeBreakdowns.by_reseller || []).slice(0, 6).map((row) => (
                        <div key={`res-${row.key}`} className="flex items-center justify-between text-xs border border-white/10 rounded px-2 py-1">
                          <span className="text-gray-300">{row.key}</span>
                          <span className="text-white font-medium">{fmtCurrency(row.net)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4">
                  <p className="text-sm text-gray-300 mb-2">Breakdown Site</p>
                  {(financeBreakdowns.by_site || []).length === 0 ? (
                    <p className="text-xs text-gray-500">Belum ada data site.</p>
                  ) : (
                    <div className="space-y-2">
                      {(financeBreakdowns.by_site || []).slice(0, 6).map((row) => (
                        <div key={`site-${row.key}`} className="flex items-center justify-between text-xs border border-white/10 rounded px-2 py-1">
                          <span className="text-gray-300">{row.key}</span>
                          <span className="text-white font-medium">{fmtCurrency(row.net)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'topup_reseller' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">Referensi income topup reseller dan beban komisi reseller.</p>
              <div className="ktc-pagination-bar flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs">
                <p>Menampilkan {(topupPage - 1) * topupPerPage + 1}-{Math.min(topupPage * topupPerPage, topupReseller.length)} dari {topupReseller.length}</p>
                <div className="flex items-center gap-2">
                  <select value={String(topupPerPage)} onChange={(e) => setTopupPerPage(Number(e.target.value))} className="px-2 py-1 rounded bg-[#0A0A0A] border border-white/10 text-white">
                    <option value="10">10 / halaman</option>
                    <option value="20">20 / halaman</option>
                    <option value="50">50 / halaman</option>
                  </select>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={topupPage <= 1} onClick={() => setTopupPage(1)}>First</Button>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={topupPage <= 1} onClick={() => setTopupPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <span>Hal {topupPage}/{totalTopupPages}</span>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={topupPage >= totalTopupPages} onClick={() => setTopupPage((p) => Math.min(totalTopupPages, p + 1))}>Next</Button>
                  <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={topupPage >= totalTopupPages} onClick={() => setTopupPage(totalTopupPages)}>Last</Button>
                </div>
              </div>
              <div className="rounded-lg border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] text-gray-300">
                    <tr>
                      <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left p-3">Tanggal</th>
                      <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left p-3">Owner/Reseller</th>
                      <th className="sticky top-0 z-10 bg-[#0A0A0A] text-left p-3">Status</th>
                      <th className="sticky top-0 z-10 bg-[#0A0A0A] text-right p-3">Nominal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingReseller ? (
                      <tr><td colSpan={4} className="p-4 text-gray-500">Memuat data...</td></tr>
                    ) : topupReseller.length === 0 ? (
                      <tr><td colSpan={4} className="p-4 text-gray-500">Belum ada data topup reseller.</td></tr>
                    ) : pagedTopupReseller.map((row) => (
                      <tr key={row.id} className="border-t border-white/10">
                        <td className="p-3 text-gray-300">{String(row.created_at || '').slice(0, 19).replace('T', ' ') || '-'}</td>
                        <td className="p-3 text-white">{row.owner_name || row.owner_id || '-'}</td>
                        <td className="p-3 text-gray-300">{row.status || '-'}</td>
                        <td className="p-3 text-right text-green-300 font-semibold">{fmtCurrency(row.commission_amount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'pengeluaran' && (
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-1 space-y-3">
                <Input placeholder="Nama pengeluaran" value={expenseForm.name} onChange={(e) => setExpenseForm((p) => ({ ...p, name: e.target.value }))} className="bg-[#0A0A0A] border-white/10 text-white" />
                <Input type="number" placeholder="Nominal" value={expenseForm.amount} onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))} className="bg-[#0A0A0A] border-white/10 text-white" />
                <Input placeholder="Catatan (opsional)" value={expenseForm.note} onChange={(e) => setExpenseForm((p) => ({ ...p, note: e.target.value }))} className="bg-[#0A0A0A] border-white/10 text-white" />
                <Button onClick={handleSaveExpense} className="btn-primary w-full"><Wallet className="w-4 h-4 mr-2" />Tambah Pengeluaran</Button>
              </div>
              <div className="lg:col-span-2 rounded-lg border border-white/10 overflow-hidden">
                <div className="ktc-pagination-bar flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2 text-xs">
                  <p>Menampilkan {(expensePage - 1) * expensePerPage + 1}-{Math.min(expensePage * expensePerPage, expenses.length)} dari {expenses.length}</p>
                  <div className="flex items-center gap-2">
                    <select value={String(expensePerPage)} onChange={(e) => setExpensePerPage(Number(e.target.value))} className="px-2 py-1 rounded bg-[#0A0A0A] border border-white/10 text-white">
                      <option value="10">10 / halaman</option>
                      <option value="20">20 / halaman</option>
                      <option value="50">50 / halaman</option>
                    </select>
                    <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={expensePage <= 1} onClick={() => setExpensePage(1)}>First</Button>
                    <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={expensePage <= 1} onClick={() => setExpensePage((p) => Math.max(1, p - 1))}>Prev</Button>
                    <span>Hal {expensePage}/{totalExpensePages}</span>
                    <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={expensePage >= totalExpensePages} onClick={() => setExpensePage((p) => Math.min(totalExpensePages, p + 1))}>Next</Button>
                    <Button type="button" size="sm" variant="outline" className="btn-secondary h-8 px-3 text-xs" disabled={expensePage >= totalExpensePages} onClick={() => setExpensePage(totalExpensePages)}>Last</Button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] text-gray-300">
                    <tr><th className="sticky top-0 z-10 bg-[#0A0A0A] text-left p-3">Nama</th><th className="sticky top-0 z-10 bg-[#0A0A0A] text-left p-3">Catatan</th><th className="sticky top-0 z-10 bg-[#0A0A0A] text-right p-3">Nominal</th><th className="sticky top-0 z-10 bg-[#0A0A0A] text-right p-3">Aksi</th></tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr><td colSpan={5} className="p-4 text-gray-500">Belum ada data pengeluaran.</td></tr>
                    ) : pagedExpenses.map((item) => (
                      <tr key={item.id} className="border-t border-white/10">
                        <td className="p-3 text-white">{item.name}</td>
                        <td className="p-3 text-gray-300">
                          {(item.note || item.notes || '-')}
                          <div className="mt-1">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${
                              ['approved', 'active'].includes(String(item?.approval_status || item?.status || '').toLowerCase())
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                                : String(item?.approval_status || '').toLowerCase() === 'rejected'
                                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                                  : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                            }`}>
                              {String(item?.approval_status || item?.status || 'pending_approval').replaceAll('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-right text-yellow-300 font-semibold">{fmtCurrency(item.amount)}</td>
                        <td className="p-3 text-right">
                          {String(item?.approval_status || '').toLowerCase().startsWith('pending') ? (
                            <div className="inline-flex gap-1">
                              <Button variant="outline" size="sm" className="text-emerald-300 border-emerald-400/30 hover:bg-emerald-500/10" onClick={() => handleApproveExpense(item.id)}>Approve</Button>
                              <Button variant="outline" size="sm" className="text-amber-300 border-amber-400/30 hover:bg-amber-500/10" onClick={() => handleRejectExpense(item.id)}>Reject</Button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-slate-500">-</span>
                          )}
                        </td>
                        <td className="p-3 text-right"><Button variant="outline" size="sm" className="text-red-300 border-red-400/30 hover:bg-red-500/10" onClick={() => handleDeleteExpense(item.id)}>Hapus</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'laba_rugi' && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Pendapatan Kotor</p><p className="text-2xl font-bold text-cyan-300 mt-1">{fmtCurrency(grossIncome)}</p></div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Total Pengeluaran</p><p className="text-2xl font-bold text-yellow-300 mt-1">{fmtCurrency(totalExpense)}</p></div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Laba Bersih</p><p className="text-2xl font-bold text-green-300 mt-1">{fmtCurrency(netIncome)}</p></div>
            </div>
          )}

          {tab === 'bhp_uso' && (
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="staff-form-label">Persentase BHP (%)</p><Input value={bhpPercent} onChange={(e) => setBhpPercent(e.target.value)} className="bg-[#050505] border-white/10 text-white" /></div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="staff-form-label">Persentase USO (%)</p><Input value={usoPercent} onChange={(e) => setUsoPercent(e.target.value)} className="bg-[#050505] border-white/10 text-white" /></div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Nilai BHP</p><p className="text-xl font-bold text-amber-300 mt-1">{fmtCurrency(bhpAmount)}</p></div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-4"><p className="text-gray-400 text-sm">Nilai USO</p><p className="text-xl font-bold text-amber-300 mt-1">{fmtCurrency(usoAmount)}</p></div>
            </div>
          )}
        </div>
                  </PageContainer>
    </AppShell>
    </>
  );
};

export default AdminFinance;













